import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  ApiError,
  type GameStatus,
  type GameSummary,
  type RulePreset,
  type Sport,
  type Team,
  type Venue,
} from '../api';
import { AppShell } from '../components/AppShell';
import { RemoteCodePanel } from '../components/RemoteCodePanel';

export function GamesPage() {
  const [games, setGames] = useState<GameSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function load() {
    try {
      setErr(null);
      setGames(await api.listGames());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Games</h1>
            <p className="mt-1 text-muted">
              Schedule a game, assign it to a display, start scoring.
            </p>
          </div>
          {!showForm && (
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>
              + Create game
            </button>
          )}
        </div>

        {showForm && (
          <CreateGameForm
            onDone={async () => {
              setShowForm(false);
              await load();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}

        {!games && !err && <div className="text-muted">Loading…</div>}

        {games && games.length === 0 && !showForm && (
          <div className="card text-center">
            <div className="mx-auto mb-3 text-4xl">🏀</div>
            <h3 className="text-lg font-semibold">No games yet</h3>
            <p className="mt-1 text-sm text-muted">
              Pick a sport, two teams, a venue, and a tip-off time.
            </p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => setShowForm(true)}
            >
              + Create your first game
            </button>
          </div>
        )}

        {games && games.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {games.map((g) => (
              <GameCard key={g.id} game={g} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function statusBadge(s: GameStatus): { label: string; className: string } {
  switch (s) {
    case 'LIVE':
      return {
        label: 'LIVE',
        className: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
      };
    case 'SCHEDULED':
      return {
        label: 'Scheduled',
        className: 'border-brand/40 bg-brand/10 text-brand-light',
      };
    case 'FINAL':
      return {
        label: 'Final',
        className: 'border-white/15 bg-white/5 text-muted',
      };
    case 'CANCELLED':
      return {
        label: 'Cancelled',
        className: 'border-red-500/30 bg-red-500/10 text-red-300',
      };
  }
}

function GameCard({ game }: { game: GameSummary }) {
  const badge = statusBadge(game.status);
  const home = game.currentState?.home.score ?? 0;
  const away = game.currentState?.away.score ?? 0;
  const [showRemote, setShowRemote] = useState(false);

  return (
    <div className="card flex flex-col gap-4">
      <Link to={`/games/${game.id}`} className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-0.5 text-xs font-semibold uppercase tracking-wider ${badge.className}`}
          >
            {game.status === 'LIVE' && (
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
            )}
            {badge.label}
          </span>
          <span className="text-xs text-muted">{game.sport.name}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <TeamCol team={game.homeTeam} score={home} />
          <span className="text-muted">–</span>
          <TeamCol team={game.awayTeam} score={away} />
        </div>
        <div className="text-xs text-muted">
          {game.venue.name}
          {game.scheduledAt && ` · ${new Date(game.scheduledAt).toLocaleString()}`}
        </div>
      </Link>
      <div className="flex flex-wrap gap-3 border-t border-border pt-3 text-xs">
        <Link
          to={`/games/${game.id}`}
          className="font-semibold text-brand-light hover:text-brand"
        >
          Score / clock →
        </Link>
        <Link
          to={`/games/${game.id}/stats`}
          className="font-semibold text-emerald-300 hover:text-emerald-200"
        >
          Stats keeper →
        </Link>
        <button
          type="button"
          onClick={() => setShowRemote(true)}
          className="ml-auto font-semibold text-muted hover:text-text"
        >
          ⧉ Generate remote code
        </button>
      </div>
      {showRemote && (
        <RemoteCodePanel
          gameId={game.id}
          onClose={() => setShowRemote(false)}
        />
      )}
    </div>
  );
}

function TeamCol({
  team,
  score,
}: {
  team: GameSummary['homeTeam'];
  score: number;
}) {
  return (
    <div className="min-w-0 flex-1 text-center">
      <div className="truncate text-sm font-semibold">{team.name}</div>
      <div className="mt-1 font-mono text-3xl font-bold">{score}</div>
    </div>
  );
}

function CreateGameForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [sports, setSports] = useState<Sport[] | null>(null);
  const [presets, setPresets] = useState<RulePreset[] | null>(null);
  const [venueId, setVenueId] = useState('');
  const [sportId, setSportId] = useState('');
  const [rulePresetId, setRulePresetId] = useState('');
  const [homeTeamId, setHomeTeamId] = useState('');
  const [awayTeamId, setAwayTeamId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [statsEnabled, setStatsEnabled] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.listVenues(), api.listTeams(), api.listSports()])
      .then(([v, t, s]) => {
        setVenues(v);
        setTeams(t);
        setSports(s);
        if (v[0]) setVenueId(v[0].id);
        const bb = s.find((x) => x.slug === 'basketball') ?? s[0];
        if (bb) setSportId(bb.id);
      })
      .catch(() => setErr('failed_to_load_prereqs'));
  }, []);

  // Refresh preset list whenever the sport changes (also seeds defaults
  // for this org+sport on first call).
  useEffect(() => {
    if (!sportId) return;
    api
      .listRulePresets(sportId)
      .then((list) => {
        setPresets(list);
        const def = list.find((p) => p.isDefault) ?? list[0];
        if (def) setRulePresetId(def.id);
        else setRulePresetId('');
      })
      .catch(() => setPresets([]));
  }, [sportId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (homeTeamId === awayTeamId) {
      setErr('Home and away must be different teams');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.createGame({
        venueId,
        sportId,
        homeTeamId,
        awayTeamId,
        rulePresetId: rulePresetId || undefined,
        scheduledAt: scheduledAt
          ? new Date(scheduledAt).toISOString()
          : undefined,
        statsEnabled,
      });
      onDone();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.message === 'home_and_away_must_differ'
            ? 'Home and away must be different teams.'
            : e.message
          : 'failed_to_create',
      );
    } finally {
      setBusy(false);
    }
  }

  if (!venues || !teams || !sports) {
    return <div className="card text-muted">Loading prerequisites…</div>;
  }

  if (venues.length === 0 || teams.length < 2) {
    return (
      <div className="card text-center">
        <p className="text-muted">
          You need at least one venue and two teams to create a game.
        </p>
        <div className="mt-3 flex justify-center gap-3 text-sm">
          {venues.length === 0 && (
            <Link to="/venues" className="text-brand-light hover:text-brand">
              + Add a venue
            </Link>
          )}
          {teams.length < 2 && (
            <Link to="/teams" className="text-brand-light hover:text-brand">
              + Add {teams.length === 0 ? 'two teams' : 'another team'}
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <h2 className="text-lg font-semibold">New game</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="label">Sport</label>
          <select
            className="input"
            value={sportId}
            onChange={(e) => setSportId(e.target.value)}
            required
          >
            {sports.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Venue</label>
          <select
            className="input"
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
            required
          >
            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Home team</label>
          <select
            className="input"
            value={homeTeamId}
            onChange={(e) => setHomeTeamId(e.target.value)}
            required
          >
            <option value="">Select…</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id} disabled={t.id === awayTeamId}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Away team</label>
          <select
            className="input"
            value={awayTeamId}
            onChange={(e) => setAwayTeamId(e.target.value)}
            required
          >
            <option value="">Select…</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id} disabled={t.id === homeTeamId}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="label">Level / rule set</label>
          <select
            className="input"
            value={rulePresetId}
            onChange={(e) => setRulePresetId(e.target.value)}
          >
            <option value="">— Sport defaults —</option>
            {(presets ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ·{' '}
                {p.config.periodCount}×
                {Math.floor(p.config.periodLengthSeconds / 60)}min
                {p.config.shotClock.enabled
                  ? ` · ${p.config.shotClock.durationSeconds}s shot clock`
                  : ' · no shot clock'}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted">
            Manage levels at{' '}
            <Link to="/settings/game-rules" className="text-brand-light hover:text-brand">
              Settings → Rules
            </Link>
            . The chosen level is snapshotted into the game — editing the
            preset later won't change finished games.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className="label">Tip-off (optional)</label>
          <input
            type="datetime-local"
            className="input"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <label className="flex items-center gap-2.5">
            <input
              type="checkbox"
              checked={statsEnabled}
              onChange={(e) => setStatsEnabled(e.target.checked)}
              className="h-4 w-4 accent-brand"
            />
            <span className="text-sm">
              Track player stats
              <span className="ml-1 text-xs text-muted">
                — enables the Stats Keeper console (rebounds, assists,
                shooting, etc.)
              </span>
            </span>
          </label>
        </div>
      </div>
      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create game'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
